import initials from "initials";
import { types } from "mobx-state-tree";
import { isAuthenticatedTeacher } from "../../lib/auth";
import { UserTypeEnum } from "./user-types";
import { getGroupOwnerId } from "../document/document-axes";
export const UserPortalOffering = types
    .model("UserPortalOffering", {
    classId: "",
    classHash: "",
    className: "",
    classUrl: "",
    teacher: "",
    activityTitle: "",
    activityUrl: "",
    problemOrdinal: "",
    unitCode: "",
    offeringId: "",
    location: ""
})
    .views(self => ({
    get problemPath() {
        const separator = self.unitCode && self.problemOrdinal ? "/" : "";
        return `${self.unitCode}${separator}${self.problemOrdinal.replace(".", "/")}`;
    }
}));
const undefinedStandaloneAuth = undefined;
const undefinedStandaloneAuthUser = undefined;
export const UserModel = types
    .model("User", {
    authenticated: false,
    id: "0",
    type: types.maybe(UserTypeEnum),
    name: "Anonymous User",
    className: "",
    classHash: "",
    offeringId: "",
    // This is the last group the user joined. It is synced with the latestGroupId
    // property in Firebase
    latestGroupId: types.maybe(types.string),
    // This is the group of this user in the particular offering that is being
    // run right now. This is the property you usually will want to use.
    // latestGroupId could be referring to a group from a different assignment/offering
    currentGroupId: types.maybe(types.string),
    portal: "",
    network: types.maybe(types.string),
    networks: types.array(types.string),
    loggingRemoteEndpoint: types.maybe(types.string),
    portalClassOfferings: types.array(UserPortalOffering),
    demoClassHashes: types.array(types.string),
    lastSupportViewTimestamp: types.maybe(types.number),
    lastStickyNoteViewTimestamp: types.maybe(types.number)
})
    .volatile(self => ({
    // in standalone mode, this is defined with a state value
    standaloneAuth: undefinedStandaloneAuth,
    // at the end of the standalone authentication process, this is set to the JWT of the learner or student
    standaloneAuthUser: undefinedStandaloneAuthUser,
    isFirebaseConnected: false,
    // number of firebase disconnects encountered during the current session
    firebaseDisconnects: 0,
    isLoggingConnected: false,
    // number of logging disconnects encountered during the current session
    loggingDisconnects: 0,
    // number of network status alerts presented during the current session
    networkStatusAlerts: 0
}))
    .actions((self) => ({
    setName(name) {
        self.name = name;
    },
    setAuthenticated(auth) {
        self.authenticated = auth;
    },
    setClassName(className) {
        self.className = className;
    },
    setLatestGroupId(latestGroupId) {
        self.latestGroupId = latestGroupId;
    },
    setCurrentGroupId(currentGroupId) {
        self.currentGroupId = currentGroupId;
    },
    setId(id) {
        self.id = id;
    },
    setType(type) {
        self.type = type;
    },
    setNetworks(network, networks) {
        self.network = network;
        self.networks.push(...networks);
    },
    setAuthenticatedUser(user) {
        var _a;
        self.authenticated = true;
        self.name = user.fullName;
        self.latestGroupId = undefined;
        self.id = user.id;
        self.portal = user.portal;
        self.type = user.type;
        if (isAuthenticatedTeacher(user)) {
            self.network = user.network;
            if (user.networks) {
                self.networks.push(...user.networks);
            }
        }
        self.className = user.className;
        self.classHash = user.classHash;
        self.offeringId = user.offeringId;
        if (user.firebaseJWT && user.firebaseJWT.returnUrl) {
            self.loggingRemoteEndpoint = user.firebaseJWT.returnUrl;
        }
        if (user.portalClassOfferings) {
            self.portalClassOfferings.replace(user.portalClassOfferings);
        }
        if ((_a = user.demoClassHashes) === null || _a === void 0 ? void 0 : _a.length) {
            self.demoClassHashes.replace(user.demoClassHashes);
        }
    },
    setStandaloneAuth(value) {
        self.standaloneAuth = value;
    },
    setStandaloneAuthUser(value) {
        self.standaloneAuthUser = value;
    },
    setIsFirebaseConnected(connected) {
        if (self.isFirebaseConnected && !connected)
            ++self.firebaseDisconnects;
        self.isFirebaseConnected = connected;
    },
    setIsLoggingConnected(connected) {
        if (self.isLoggingConnected && !connected)
            ++self.loggingDisconnects;
        self.isLoggingConnected = connected;
    },
    incrementNetworkStatusAlertCount() {
        ++self.networkStatusAlerts;
    },
    setLastSupportViewTimestamp(timestamp) {
        self.lastSupportViewTimestamp = timestamp;
    },
    setLastStickyNoteViewTimestamp(timestamp) {
        self.lastStickyNoteViewTimestamp = timestamp;
    }
}))
    .views((self) => ({
    get isStudent() {
        return self.type === "student";
    },
    get isTeacher() {
        return self.type === "teacher";
    },
    get isResearcher() {
        return self.type === "researcher";
    },
    get isTeacherOrResearcher() {
        return (self.type === "teacher") || (self.type === "researcher");
    },
    get isNetworkedTeacher() {
        return (self.type === "teacher") && !!self.network;
    },
    get initials() {
        return initials(self.name);
    },
    get activityUrl() {
        const offering = self.portalClassOfferings.find(o => o.offeringId === self.offeringId);
        return offering === null || offering === void 0 ? void 0 : offering.activityUrl;
    },
    /**
     * Group documents have a special user id based on the offering and group id.
     * In this way every member of the group will have the same userId for group documents.
     *
     * Undefined when this user is not in a group in an offering: there is no group whose documents these
     * would be.
     */
    get userIdForGroupDocuments() {
        if (!self.offeringId || !self.currentGroupId)
            return undefined;
        return getGroupOwnerId(self.offeringId, self.currentGroupId);
    }
}))
    .views((self) => ({
    classHashesForProblemPath(problemPath) {
        const classSet = new Set([self.classHash]);
        if (self.isTeacher) {
            // authenticated teachers pull class hashes from portalClassOfferings
            if (self.portalClassOfferings.length) {
                self.portalClassOfferings.forEach(o => {
                    if (o.classHash && (o.problemPath === problemPath)) {
                        classSet.add(o.classHash);
                    }
                });
            }
            // non-authenticated teachers (e.g. demo/qa) pull from demoClassHashes
            else if (self.demoClassHashes.length) {
                self.demoClassHashes.forEach(hash => classSet.add(hash));
            }
        }
        return [...classSet];
    },
}));
//# sourceMappingURL=data:application/json;base64,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